/**
Author - Brijesh Pandey
Git - https://bitbucket.org/__brijesh/
**/

import React, {useState, useEffect} from 'react';

import styled from 'styled-components';

import {getPersonalDetailSchema, updatePersonalDetails} from 'api';

import {handleApiError, triggerToast} from 'helpers';

import PersonalDetails from './PersonalDetails';
import {ProgressBar} from 'components/ProgressBar';

const PersonalDetailsContainer = ({updateStatus}) => {
  const [formSchema, setFormSchema] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSchema();
  }, []);

  const fetchSchema = async () => {
    try {
      setIsLoading(true);
      const response = await getPersonalDetailSchema();
      setFormSchema(response.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (data) => {
    const pincodeRegex = /^\d{6}$/;

    const isInvalidPincode = (country, pincode) =>
      country?.toLowerCase() === 'india' && pincode && !pincodeRegex.test(pincode);

    if (isInvalidPincode(data.per_add_country, data.per_add_pincode)) {
      triggerToast('Pincode must be 6 digits for Permanent Address', 'error');
      return false;
    }

    if (isInvalidPincode(data.corr_add_country, data.corr_add_pincode)) {
      triggerToast('Pincode must be 6 digits for Correspondence Address', 'error');
      return false;
    }

    return true;
  };

  const handleFormSubmit = async (data) => {
    let resolvedData = {...data};

    const isCopyFromPermanent = Array.isArray(resolvedData.copy_from_permanent)
      ? resolvedData.copy_from_permanent.includes('true')
      : resolvedData.copy_from_permanent === 'true' || resolvedData.copy_from_permanent === true;

    if (isCopyFromPermanent) {
      resolvedData.corr_add_line1 = resolvedData.per_add_line1;
      resolvedData.corr_add_line2 = resolvedData.per_add_line2;
      resolvedData.corr_add_line3 = resolvedData.per_add_line3;
      resolvedData.corr_add_pincode = resolvedData.per_add_pincode;
      resolvedData.corr_add_district_or_city = resolvedData.per_add_district_or_city;
      resolvedData.corr_add_country = resolvedData.per_add_country;
    }

    if (!validateForm(resolvedData)) return;

    try {
      setIsLoading(true);
      const payload = {
        ...resolvedData,
        mothers_name: resolvedData.mothers_name?.toUpperCase(),
        fathers_name: resolvedData.fathers_name?.toUpperCase(),
        full_name: resolvedData.full_name?.toUpperCase(),
      };

      const res = await updatePersonalDetails(payload);
      if (res.status == 200) {
        updateStatus();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return isLoading ? (
    <LoaderContainer>
      <ProgressBar
        showShadow={false}
        subheading="Please wait while we fetch your details"
        heading="Fetching your details"
        waitTimeInSec={30}
        stopOnEndTime={true}
        stopOnEndMsg={'Something went wrong. Please try again...'}
      />
    </LoaderContainer>
  ) : (
    <PersonalDetails
      formSchema={formSchema?.schema}
      addressOptions={formSchema?.addresses}
      handleFormSubmit={handleFormSubmit}
    />
  );
};

const LoaderContainer = styled.div`
  height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export default PersonalDetailsContainer;
